import { useEffect, useState } from 'react'
import { useObjectGet } from '../api/object'
import {
	OverspeedPeriod,
	OverspeedResult,
	useReservationOverspeed,
	VehicleLocationResult,
} from '../api/overspeed'
import { Button } from '../components/button'
import { Dialog } from '../components/dialog'
import { get } from '../fetch'
import { useToggle } from '../use-toggle'

const LOCATION_ERROR: Record<string, string> = {
	not_configured: 'Napojení na gps-server.net není nakonfigurováno.',
	no_device: 'U vozidla není vyplněno pole „GPS zařízení (IMEI)".',
	no_position: 'gps-server nevrátil aktuální polohu vozidla.',
	not_found: 'Rezervace nenalezena.',
}

export function VehicleLocationButton({ reservationId }: { reservationId: string }) {
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const onPress = async () => {
		setError(null)
		setLoading(true)
		// Záložku otevři hned (v rámci kliknutí) kvůli blokování pop-up oken.
		const win = window.open('', '_blank')
		try {
			const result = await get<VehicleLocationResult>(
				`/api/0/reservation/${reservationId}/location`,
			)
			if (result.status === 'ok' && result.lat != null && result.lng != null) {
				const url = `https://www.google.com/maps?q=${result.lat},${result.lng}`
				if (win) win.location.href = url
				else window.open(url, '_blank')
			} else {
				win?.close()
				setError(LOCATION_ERROR[result.status] ?? 'Polohu se nepodařilo získat.')
			}
		} catch (e) {
			win?.close()
			setError((e as { message?: string })?.message ?? 'Polohu se nepodařilo získat.')
		} finally {
			setLoading(false)
		}
	}

	return (
		<div className="flex flex-col items-start">
			<Button variant="SECONDARY" onPress={onPress} isDisabled={loading}>
				{loading ? 'Hledám polohu…' : 'Poslední poloha'}
			</Button>
			{error ? <p className="mt-1 text-xs text-rose-700">{error}</p> : null}
		</div>
	)
}

function formatDateTime(iso: string): string {
	const date = new Date(iso)
	if (Number.isNaN(date.getTime())) return iso
	return date.toLocaleString('cs-CZ', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	})
}

function formatDuration(seconds: number): string {
	const s = Math.max(0, Math.round(seconds))
	if (s < 60) return `${s} s`
	const m = Math.floor(s / 60)
	const rest = s % 60
	if (m < 60) return rest ? `${m} min ${rest} s` : `${m} min`
	const h = Math.floor(m / 60)
	return `${h} h ${m % 60} min`
}

function categoryLabel(category?: string | null): string {
	if (!category) return 'Nedefinováno'
	return category.charAt(0).toUpperCase() + category.slice(1)
}

/** ISO (UTC) → hodnota pro <input type="datetime-local"> v lokálním čase. */
function isoToLocalInput(iso: string): string {
	const date = new Date(iso)
	if (Number.isNaN(date.getTime())) return ''
	const pad = (n: number) => String(n).padStart(2, '0')
	return (
		`${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
		`T${pad(date.getHours())}:${pad(date.getMinutes())}`
	)
}

function escapeHtml(value: string): string {
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
}

function buildPrintHtml(reservationId: string, result: OverspeedResult): string {
	const rows =
		result.status === 'ok'
			? (result.incidents ?? [])
					.map(
						i => `<tr>
							<td>${escapeHtml(formatDateTime(i.from))}</td>
							<td>${escapeHtml(formatDateTime(i.to))}</td>
							<td>${escapeHtml(formatDuration(i.durationSeconds))}</td>
							<td class="speed">${i.maxSpeed} km/h</td>
							<td>${i.lat != null && i.lng != null ? `${i.lat}, ${i.lng}` : '—'}</td>
						</tr>`,
					)
					.join('')
			: ''

	const summary =
		result.status === 'ok'
			? `<table class="summary">
					<tr><th>${result.custom ? 'Kontrolovaný termín' : 'Kontrolovaný termín pronájmu'}</th><td>${
						result.period
							? `${escapeHtml(formatDateTime(result.period.from))} – ${escapeHtml(
									formatDateTime(result.period.to),
								)}`
							: '—'
					}</td></tr>
					<tr><th>Počet překročení</th><td>${result.incidentCount ?? 0}</td></tr>
					<tr><th>Nejvyšší rychlost</th><td>${
						result.maxSpeed != null ? `${result.maxSpeed} km/h` : '—'
					}</td></tr>
					<tr><th>Limit (${escapeHtml(categoryLabel(result.category))})</th><td>${result.limit} km/h</td></tr>
					<tr><th>Počet záznamů</th><td>${result.sampleCount ?? 0}</td></tr>
				</table>
				${
					rows
						? `<table class="incidents">
								<thead><tr><th>Od</th><th>Do</th><th>Trvání</th><th>Max. rychlost</th><th>Místo (lat, lng)</th></tr></thead>
								<tbody>${rows}</tbody>
							</table>`
						: '<p>Žádné překročení limitu nebylo zjištěno.</p>'
				}`
			: `<p>Kontrolu nelze provést (chybí konfigurace gps-server.net nebo IMEI vozidla).</p>`

	return `<!doctype html><html lang="cs"><head><meta charset="utf-8">
		<title>Překročení rychlosti – rezervace ${escapeHtml(reservationId)}</title>
		<style>
			body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 32px; }
			h1 { font-size: 18px; margin: 0 0 4px; }
			.sub { color: #555; font-size: 12px; margin: 0 0 20px; }
			table { border-collapse: collapse; width: 100%; margin-bottom: 20px; font-size: 13px; }
			.summary th { text-align: left; width: 240px; color: #555; font-weight: normal; padding: 4px 8px; }
			.summary td { padding: 4px 8px; font-weight: bold; }
			.incidents th, .incidents td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
			.incidents thead th { background: #f3f4f6; }
			.incidents .speed { font-weight: bold; color: #b91c1c; }
			@media print { body { margin: 12mm; } }
		</style></head>
		<body>
			<h1>Kontrola překročení rychlosti</h1>
			<p class="sub">Rezervace ID: ${escapeHtml(reservationId)} · vytištěno ${escapeHtml(
				new Date().toLocaleString('cs-CZ'),
			)}</p>
			${summary}
			<script>window.onload = function () { window.print() }</script>
		</body></html>`
}

function printResult(reservationId: string, result: OverspeedResult) {
	const win = window.open('', '_blank', 'width=900,height=700')
	if (!win) return
	win.document.write(buildPrintHtml(reservationId, result))
	win.document.close()
	win.focus()
}

function StatusMessage({ result }: { result: OverspeedResult }) {
	if (result.status === 'not_configured') {
		return (
			<p className="text-sm text-amber-700">
				Napojení na gps-server.net není nakonfigurováno (chybí GPS_SERVER_HOST nebo
				GPS_SERVER_API_KEY).
			</p>
		)
	}
	if (result.status === 'no_device') {
		return (
			<p className="text-sm text-amber-700">
				U vozidla není vyplněno pole „GPS zařízení (IMEI)". Doplňte ho v detailu vozidla.
			</p>
		)
	}
	if (result.status === 'invalid_period') {
		return (
			<p className="text-sm text-rose-700">{result.message ?? 'Neplatný termín kontroly.'}</p>
		)
	}
	return null
}

function Result({ result }: { result: OverspeedResult }) {
	if (result.status !== 'ok') return <StatusMessage result={result} />

	const incidents = result.incidents ?? []
	const hasOverspeed = (result.incidentCount ?? 0) > 0

	return (
		<div className="space-y-4">
			{result.period ? (
				<div className="rounded-lg bg-gray-50 px-4 py-2 text-sm text-gray-700">
					<span className="text-gray-500">
						{result.custom ? 'Kontrolovaný termín: ' : 'Kontrolovaný termín pronájmu: '}
					</span>
					<span className="font-medium">
						{formatDateTime(result.period.from)} – {formatDateTime(result.period.to)}
					</span>
				</div>
			) : null}
			<div
				className={`rounded-lg px-4 py-3 text-sm ${
					hasOverspeed ? 'bg-rose-50 text-rose-800' : 'bg-emerald-50 text-emerald-800'
				}`}
			>
				{hasOverspeed ? (
					<span className="font-semibold">
						Zjištěno {result.incidentCount}× překročení limitu {result.limit} km/h.
					</span>
				) : (
					<span className="font-semibold">
						Žádné překročení limitu {result.limit} km/h.
					</span>
				)}
			</div>

			<dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-gray-700">
				<dt className="text-gray-500">Nejvyšší rychlost</dt>
				<dd className="font-medium">
					{result.maxSpeed != null ? `${result.maxSpeed} km/h` : '—'}
				</dd>
				<dt className="text-gray-500">Limit ({categoryLabel(result.category)})</dt>
				<dd className="font-medium">{result.limit} km/h</dd>
				<dt className="text-gray-500">Počet záznamů</dt>
				<dd className="font-medium">{result.sampleCount ?? 0}</dd>
			</dl>

			{incidents.length > 0 ? (
				<div className="overflow-x-auto">
					<table className="w-full text-left text-sm">
						<thead className="text-gray-500">
							<tr>
								<th className="py-1 pr-3 font-medium">Od</th>
								<th className="py-1 pr-3 font-medium">Do</th>
								<th className="py-1 pr-3 font-medium">Trvání</th>
								<th className="py-1 pr-3 font-medium">Max. rychlost</th>
								<th className="py-1 pr-3 font-medium">Místo</th>
							</tr>
						</thead>
						<tbody>
							{incidents.map((incident, index) => (
								<tr key={index} className="border-t border-gray-100">
									<td className="py-1.5 pr-3">{formatDateTime(incident.from)}</td>
									<td className="py-1.5 pr-3">{formatDateTime(incident.to)}</td>
									<td className="py-1.5 pr-3">
										{formatDuration(incident.durationSeconds)}
									</td>
									<td className="py-1.5 pr-3 font-semibold text-rose-700">
										{incident.maxSpeed} km/h
									</td>
									<td className="py-1.5 pr-3">
										{incident.lat != null && incident.lng != null ? (
											<a
												className="text-blue-600 hover:underline"
												href={`https://www.google.com/maps?q=${incident.lat},${incident.lng}`}
												target="_blank"
												rel="noreferrer"
											>
												mapa
											</a>
										) : (
											'—'
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			) : null}
		</div>
	)
}

function PeriodPicker({
	mode,
	fromInput,
	toInput,
	inputError,
	isFetching,
	onSelectCustom,
	onChangeFrom,
	onChangeTo,
	onRunRental,
	onApplyCustom,
}: {
	mode: 'rental' | 'custom'
	fromInput: string
	toInput: string
	inputError: string | null
	isFetching: boolean
	onSelectCustom: () => void
	onChangeFrom: (value: string) => void
	onChangeTo: (value: string) => void
	onRunRental: () => void
	onApplyCustom: () => void
}) {
	const tab = (active: boolean) =>
		`rounded-md px-3 py-1.5 text-sm font-medium ${
			active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
		}`

	return (
		<div className="space-y-3 rounded-lg border border-gray-200 p-3">
			<div className="flex gap-2">
				<button
					type="button"
					className={tab(mode === 'rental')}
					onClick={onRunRental}
					disabled={isFetching}
				>
					Za celou dobu pronájmu
				</button>
				<button type="button" className={tab(mode === 'custom')} onClick={onSelectCustom}>
					Vlastní termín
				</button>
			</div>

			{mode === 'custom' ? (
				<div className="space-y-2">
					<div className="flex flex-wrap items-end gap-3">
						<label className="text-sm text-gray-600">
							<span className="mb-1 block">Od</span>
							<input
								type="datetime-local"
								value={fromInput}
								onChange={e => onChangeFrom(e.target.value)}
								className="rounded-md border border-gray-300 px-2 py-1 text-sm"
							/>
						</label>
						<label className="text-sm text-gray-600">
							<span className="mb-1 block">Do</span>
							<input
								type="datetime-local"
								value={toInput}
								onChange={e => onChangeTo(e.target.value)}
								className="rounded-md border border-gray-300 px-2 py-1 text-sm"
							/>
						</label>
						<Button
							variant="PRIMARY"
							size="SMALL"
							onPress={onApplyCustom}
							isDisabled={isFetching}
						>
							Zobrazit
						</Button>
					</div>
					{inputError ? <p className="text-sm text-rose-700">{inputError}</p> : null}
				</div>
			) : null}
		</div>
	)
}

export function OverspeedButton({ reservationId }: { reservationId: string }) {
	const [isOpen, toggleOpen] = useToggle()
	const [mode, setMode] = useState<'rental' | 'custom'>('rental')
	const [fromInput, setFromInput] = useState('')
	const [toInput, setToInput] = useState('')
	const [appliedPeriod, setAppliedPeriod] = useState<OverspeedPeriod | null>(null)
	const [inputError, setInputError] = useState<string | null>(null)
	// Dotaz na gps-server se spustí až po prvním kliknutí na „Zobrazit".
	const [hasRun, setHasRun] = useState(false)

	// Rezervace (z cache) jen pro předvyplnění vlastního termínu – bez GPS dotazu.
	const { data: reservation } = useObjectGet('reservation', reservationId)

	const { data, isFetching, isError, error, refetch } = useReservationOverspeed(
		reservationId,
		isOpen && hasRun,
		appliedPeriod,
	)

	// Předvyplň vlastní termín z období pronájmu.
	useEffect(() => {
		const resv = reservation as { date_from?: string; date_to?: string } | undefined
		if (resv?.date_from && resv?.date_to && !fromInput && !toInput) {
			setFromInput(isoToLocalInput(resv.date_from))
			setToInput(isoToLocalInput(resv.date_to))
		}
	}, [reservation, fromInput, toInput])

	const runRental = () => {
		setInputError(null)
		setMode('rental')
		setAppliedPeriod(null)
		setHasRun(true)
	}

	const applyCustom = () => {
		setInputError(null)
		if (!fromInput || !toInput) {
			setInputError('Vyplňte termín od i do.')
			return
		}
		const from = new Date(fromInput)
		const to = new Date(toInput)
		if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
			setInputError('Zadejte platný termín.')
			return
		}
		if (from.getTime() >= to.getTime()) {
			setInputError('Termín „od" musí být dříve než „do".')
			return
		}
		setAppliedPeriod({ from: from.toISOString(), to: to.toISOString() })
		setHasRun(true)
	}

	const serverError =
		(error as { message?: string } | undefined)?.message ??
		'Nepodařilo se získat data z gps-server.net.'

	return (
		<>
			<Button variant="SECONDARY" onPress={() => toggleOpen(true)}>
				Překročení rychlosti
			</Button>
			{isOpen ? (
				<Dialog heading="Kontrola překročení rychlosti" isOpen onOpenChange={toggleOpen}>
					<div className="m-4 min-w-[28rem] max-w-2xl space-y-4">
						<PeriodPicker
							mode={mode}
							fromInput={fromInput}
							toInput={toInput}
							inputError={inputError}
							isFetching={isFetching}
							onSelectCustom={() => setMode('custom')}
							onChangeFrom={setFromInput}
							onChangeTo={setToInput}
							onRunRental={runRental}
							onApplyCustom={applyCustom}
						/>

						{!hasRun ? (
							<p className="text-sm text-gray-500">
								Zvol termín a klikni na „Zobrazit". Kratší termín = méně dat a
								rychlejší výsledek.
							</p>
						) : isFetching ? (
							<p className="text-sm text-gray-600">Načítám data z gps-server.net…</p>
						) : isError ? (
							<div className="space-y-3">
								<p className="text-sm text-rose-700">{serverError}</p>
								<Button variant="SECONDARY" size="SMALL" onPress={() => refetch()}>
									Zkusit znovu
								</Button>
							</div>
						) : data ? (
							<Result result={data} />
						) : null}

						{hasRun && data && data.status === 'ok' && !isFetching && !isError ? (
							<div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
								<Button
									variant="SECONDARY"
									size="SMALL"
									onPress={() => printResult(reservationId, data)}
								>
									Tisk
								</Button>
								<Button
									variant="SECONDARY"
									size="SMALL"
									onPress={() => printResult(reservationId, data)}
								>
									Uložit do PDF
								</Button>
							</div>
						) : null}
					</div>
				</Dialog>
			) : null}
		</>
	)
}
